
import { supabase } from "@/integrations/supabase/client";
import { ContentItem } from "@/components/dashboard/content-card/types";
import { Collection } from "@/contexts/collections/types";

/**
 * Utility function to fetch collection info for a specific content item
 * with improved caching and error handling
 */
export const fetchItemCollectionDetails = async (
  item: ContentItem,
  getCollectionById: (id: string) => Collection | undefined
): Promise<ContentItem> => {
  try {
    // First check if the item already has collection details
    if (item.collection_color && item.collection_id) {
      console.log(`[COLOR DEBUG] fetchItemCollectionDetails - Item ${item.id} already has collection color: ${item.collection_color}`);
      return item;
    }
    
    // Fetch the collection for this content item
    const { data: contentCollection, error: contentCollectionError } = await supabase
      .from("content_collections")
      .select("collection_id")
      .eq("content_id", item.id)
      .maybeSingle();
      
    if (contentCollectionError || !contentCollection?.collection_id) {
      console.log(`[COLOR DEBUG] fetchItemCollectionDetails - No collection found for item ${item.id}, using default color #6B7789`);
      return {
        ...item,
        collection_color: "#6B7789" // Default color
      };
    }
    
    const collectionId = contentCollection.collection_id;
    
    // First try to get from context for better performance
    const collectionFromContext = getCollectionById(collectionId);
    if (collectionFromContext) {
      const color = collectionFromContext.color || "#6B7789";
      console.log(`[COLOR DEBUG] fetchItemCollectionDetails - Found collection in context for item ${item.id}: ${collectionId}, color: ${color}`);
      return {
        ...item,
        collection_color: color,
        collection_id: collectionId
      };
    }
    
    // Fallback to DB query if not in context
    console.log(`[COLOR DEBUG] fetchItemCollectionDetails - Collection not in context, fetching from DB: ${collectionId}`);
    const { data: collectionData, error: collectionError } = await supabase
      .from("collections")
      .select("color, name")
      .eq("id", collectionId)
      .single();
      
    if (collectionError || !collectionData) {
      console.log(`[COLOR DEBUG] fetchItemCollectionDetails - Error fetching collection ${collectionId} from DB, using default color #6B7789`);
      return {
        ...item,
        collection_color: "#6B7789", // Default color
        collection_id: collectionId
      };
    }
    
    const color = collectionData.color || "#6B7789";
    console.log(`[COLOR DEBUG] fetchItemCollectionDetails - Found collection in DB: ${collectionId}, color: ${color}, name: ${collectionData.name}`);
    
    // Return the item with the collection color
    return {
      ...item,
      collection_color: color,
      collection_id: collectionId
    };
  } catch (error) {
    console.error(`[COLOR DEBUG] Unexpected error fetching collection details for item ${item.id}:`, error);
    return {
      ...item,
      collection_color: "#6B7789" // Default color in case of errors
    };
  }
};
